import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { capabilities } from "@/data/capabilities";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";

export default function About() {
  usePageMeta("About", "The person behind the projects. I have spent my career becoming the person who can understand the business conversation, participate in the technical conversation, and manage the relationship.");

  return (
    <Layout>
      <div className="container mx-auto px-6 max-w-5xl py-12 md:py-20">

        {/* Editorial two-column opening */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-24">

          {/* Left — text */}
          <div className="lg:col-span-7 space-y-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-tight">
              The person behind<br className="hidden md:block" /> the projects.
            </h1>
            <p className="text-xl text-foreground font-serif leading-relaxed border-l-4 border-accent pl-6 py-1">
              "I have spent most of my career becoming the person who can understand the business conversation, participate in the technical conversation, manage the relationship, and still make sure the work gets done."
            </p>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                I didn't start out with a master plan to be a hybrid operator. I just kept finding myself in rooms where the business team didn't understand the engineers, the engineers didn't understand the customer, and the customer didn't care about anything except whether the product worked.
              </p>
              <p>
                I learned to code because I was tired of waiting for things to be built. I learned marketing because building things no one uses is depressing. I learned operations and business development because eventually you realize that the code is only 20% of a successful product. The other 80% is the system around it: the people, the processes, the partnerships, and the economics.
              </p>
              <p>
                I live in Oceanside, California with my wife, our two kids, and our dog Butters, who also answers to Goose when she feels like it. When I'm not untangling a messy integration or mapping out a new platform architecture, I'm usually cycling, building something physical, or experimenting with whatever weird new technology has caught my attention this week.
              </p>
            </div>
          </div>

          {/* Right — photo, editorial treatment */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="relative">
              {/* Offset accent block behind photo */}
              <div className="absolute -bottom-4 -right-4 w-full h-full rounded-2xl bg-accent/10 border border-accent/20" />
              <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[3/4]">
                <img
                  src="/images/travis-portrait.png"
                  alt="Travis Johnson — Oceanside, California"
                  className="w-full h-full object-cover object-top"
                  data-testid="img-about-portrait"
                />
              </div>
              <p className="mt-4 text-xs text-muted-foreground/50 text-right tracking-wide uppercase">
                Oceanside, California
              </p>
            </div>
          </div>

        </div>

        <h2 className="text-3xl font-serif font-medium mb-8">What I Bring to the Table</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 mb-20">
          {capabilities.map((cap, i) => (
            <div key={i}>
              <h3 className="text-lg font-semibold border-b border-border pb-3 mb-4">{cap.category}</h3>
              <div className="flex flex-wrap gap-2">
                {cap.skills.map((skill, j) => (
                  <span key={j} className="text-sm px-3 py-1.5 bg-secondary/50 text-secondary-foreground rounded-md border border-border/50">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-3xl p-10 md:p-16 text-center">
          <h2 className="text-2xl md:text-3xl font-serif font-medium mb-6">Want to see the tactical details?</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            If you're looking for a detailed breakdown of my professional history, past roles, and the specific platforms I've worked with, you can view my full experience snapshot.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/experience">
              <Button size="lg" className="rounded-full px-8">View Professional Background</Button>
            </Link>
            <Link href="/contact">
              <Button variant="outline" size="lg" className="rounded-full px-8">Get in Touch</Button>
            </Link>
          </div>
        </div>

      </div>
    </Layout>
  );
}
